import type { ApiTokenDto } from '@fos/shared/contracts/tokens';
import {
  API_TOKEN_ACCESS_LABELS,
  API_TOKEN_LOCAL_RUNNER_LABEL,
  API_TOKEN_STATUS_LABELS,
  API_TOKEN_STATUS_TONES,
} from '@fos/shared/labels';
import { KeyRound, Plug, Plus } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { formatDate, formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { ListRow } from '../../../ui/ListRow';
import { Mono } from '../../../ui/Mono';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { expiryText, lastUsedText, tokenSummary } from './tokens-ui';

type View = 'active' | 'all' | 'team';

/**
 * Lista tokenów osobistych MCP: nazwa, prefiks, poziom, zakres, ostatnie użycie (data, IP), ważność, status.
 * Kliknięcie = szczegóły; „Cofnij” z potwierdzeniem działa od razu (bez potwierdzenia hasłem — cofnięcie tylko
 * odbiera dostęp). Owner / admin: widok „Zespół” z tokenami wszystkich osób.
 *
 * Props: `onCreate` — otwiera „Nowy token” (przycisk w pustym stanie).
 */
export function PersonalTokensSection({ onCreate }: { onCreate: () => void }) {
  const access = useAccess();
  const [view, setView] = useState<View>('active');
  const [details, setDetails] = useState<ApiTokenDto | null>(null);
  const [confirm, setConfirm] = useState<ApiTokenDto | null>(null);
  const utils = trpc.useUtils();
  const list = trpc.tokens.list.useQuery({
    // tokeny osobiste i połączenia konektorów (Claude przez OAuth)
    kind: 'mcp',
    includeInactive: view !== 'active',
    allMembers: view === 'team',
  });
  const revoke = trpc.tokens.revoke.useMutation({
    onSuccess: (t) => {
      toast.success(t.kind === 'oauth' ? `Odłączono „${t.name}”` : `Cofnięto token „${t.name}”`, {
        description: 'Agent traci dostęp od następnego wywołania.',
      });
      void utils.tokens.list.invalidate();
      setConfirm(null);
      setDetails(null);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const views = [
    { value: 'active' as const, label: 'Aktywne' },
    { value: 'all' as const, label: 'Wszystkie' },
    ...(access.isAdmin ? [{ value: 'team' as const, label: 'Zespół' }] : []),
  ];
  const items = list.data ?? [];

  return (
    <Section className="mt-6" aria-labelledby="mcp-tokens">
      <SectionHeader
        tone="strong"
        title={<span id="mcp-tokens">Tokeny MCP</span>}
        action={
          <Segmented
            aria-label="Pokaż"
            size="s"
            value={view}
            onValueChange={setView}
            options={views}
            className="max-md:[&>button]:h-10"
          />
        }
      />
      {list.isPending ? (
        <ListSkeleton rows={2} />
      ) : list.isError ? (
        <ErrorState compact onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : items.length === 0 ? (
        <EmptyState
          compact
          icon={KeyRound}
          title={view === 'active' ? 'Nie masz aktywnych tokenów' : 'Brak tokenów'}
          description="Utwórz token, aby podłączyć Claude Code albo innego agenta MCP."
          action={
            <Button variant="secondary" icon={Plus} onClick={onCreate} className={TOUCH_BUTTON}>
              Nowy token
            </Button>
          }
        />
      ) : (
        <div className="border-t border-line">
          {items.map((t) => (
            <ListRow
              key={t.id}
              height="l"
              onClick={() => setDetails(t)}
              leading={
                t.kind === 'oauth' ? (
                  <Plug size={18} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
                ) : (
                  <KeyRound size={18} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
                )
              }
              title={view === 'team' ? `${t.name} · ${t.user.name}` : t.name}
              description={`${tokenSummary(t)} · ${lastUsedText(t)}`}
              trailing={
                <StatusPill tone={API_TOKEN_STATUS_TONES[t.status]}>
                  {t.status === 'active' ? expiryText(t) : API_TOKEN_STATUS_LABELS[t.status]}
                </StatusPill>
              }
            />
          ))}
        </div>
      )}

      <Dialog
        open={details !== null}
        onOpenChange={(open) => !open && setDetails(null)}
        title={details?.name ?? 'Token'}
        description={details ? tokenSummary(details) : undefined}
        footer={
          details?.status === 'active' ? (
            <Button variant="secondary" onClick={() => setConfirm(details)} className={TOUCH_BUTTON}>
              {details.kind === 'oauth' ? 'Odłącz' : 'Cofnij token'}
            </Button>
          ) : undefined
        }
      >
        {details && <TokenDetails token={details} />}
      </Dialog>

      <Dialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        width="s"
        title={confirm?.kind === 'oauth' ? `Odłączyć „${confirm.name}”?` : `Cofnąć token „${confirm?.name ?? ''}”?`}
        description={
          confirm?.kind === 'oauth'
            ? 'Claude straci dostęp od następnego wywołania i nie odświeży tokenu. Połączysz go ponownie w ustawieniach konektorów Claude.'
            : 'Agent, który go używa, straci dostęp od następnego wywołania. Tego nie da się odwrócić — w razie potrzeby utworzysz nowy token.'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)} className={TOUCH_BUTTON}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              loading={revoke.isPending}
              onClick={() => confirm && revoke.mutate({ id: confirm.id })}
              className={TOUCH_BUTTON}
            >
              {confirm?.kind === 'oauth' ? 'Odłącz' : 'Cofnij token'}
            </Button>
          </>
        }
      />
    </Section>
  );
}

function TokenDetails({ token }: { token: ApiTokenDto }) {
  const rows: [string, ReactNode][] = [
    token.kind === 'oauth'
      ? (['Rodzaj', 'Konektor (OAuth) — tokeny odnawiane co godzinę'] as [string, ReactNode])
      : (['Prefiks', <Mono key="p">{token.prefix}…</Mono>] as [string, ReactNode]),
    ['Poziom', API_TOKEN_ACCESS_LABELS[token.access]],
    ...(token.kind === 'pat'
      ? ([[API_TOKEN_LOCAL_RUNNER_LABEL, token.localRunnerAllowed ? 'Tak' : 'Nie']] as [string, ReactNode][])
      : []),
    [
      'Zakres',
      token.scope.all ? (
        'Wszystko, do czego masz dostęp'
      ) : (
        <ul key="s" className="flex flex-col gap-0.5">
          {token.scope.clients.map((c) => (
            <li key={c.id}>{c.name}</li>
          ))}
          {token.scope.projects.map((p) => (
            <li key={p.id}>
              Projekt {p.name}
              {p.clientName ? <span className="text-fg-muted"> · {p.clientName}</span> : null}
            </li>
          ))}
        </ul>
      ),
    ],
    [
      'Status',
      <StatusPill key="st" tone={API_TOKEN_STATUS_TONES[token.status]}>
        {API_TOKEN_STATUS_LABELS[token.status]}
      </StatusPill>,
    ],
    ['Osoba', token.user.name],
    ['Utworzony', formatDate(token.createdAt)],
    ['Ważność', expiryText(token)],
    [
      'Ostatnie użycie',
      token.lastUsedAt ? (
        <span key="u">
          {formatRelativePast(token.lastUsedAt)}
          {token.lastUsedIp ? (
            <>
              {' '}
              · <Mono>{token.lastUsedIp}</Mono>
            </>
          ) : null}
        </span>
      ) : (
        'jeszcze nieużywany'
      ),
    ],
    ...(token.revokedAt ? ([['Cofnięty', formatDate(token.revokedAt)]] as [string, ReactNode][]) : []),
  ];
  return (
    <dl className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2.5 text-body-s">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-fg-muted">{label}</dt>
          <dd className="min-w-0 text-fg">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
